import { AuthErrorResponse, ERROR_CODES, getUserFriendlyMessage, SPECIFIC_ERROR_MESSAGES } from './errorStructure';
import { mapErrorToI18nKey, getErrorMessageKey } from './errorMapper';
import { setMessage } from '../redux/messageSlice';
import { logout } from '../redux/userSlice';
import { MiddlewareAPI } from '@reduxjs/toolkit';
import i18n from '../i18n/config';
import { getAuthErrorMapping } from '../Components/Pages/Login/authErrorConfig';

// Specific error handler for authentication endpoints
export class AuthErrorHandler {
  private api: MiddlewareAPI;

  constructor(api: MiddlewareAPI) {
    this.api = api;
  }

  // Handle sign-in specific errors
  handleSignInError(error: AuthErrorResponse | Record<string, unknown>): void {
    const err = error as Record<string, unknown>;
    const nested =
      (err?.error as { statusCode?: number } | undefined) ??
      (err?.data as Record<string, unknown> | undefined)?.error;
    const statusCode =
      (nested as { statusCode?: number } | undefined)?.statusCode ??
      (typeof err?.status === 'number' ? err.status : undefined);

    switch (statusCode) {
      case ERROR_CODES.UNAUTHORIZED:
        this.handleUnauthorizedError(error);
        break;
      
      case ERROR_CODES.VALIDATION_ERROR:
        this.handleValidationError(error);
        break;
      
      case ERROR_CODES.INTERNAL_SERVER_ERROR:
        this.handleServerError(error);
        break;
      
      case ERROR_CODES.BAD_REQUEST:
        this.handleBadRequestError(error);
        break;
      
      default:
        this.handleGenericError(error);
    }
  }

  // Handle sign-up specific errors
  // ALWAYS use code + domain for i18n lookup
  handleSignUpError(error: AuthErrorResponse): void {
    const { error: errorData } = error;

    // Use the new error mapper to get i18n key from code + domain
    const mappingResult = mapErrorToI18nKey(error);
    
    // Get translated message using domain.code pattern with fallback
    const translated = i18n.t(`error:${mappingResult.messageKey}`, { 
      defaultValue: i18n.t('error:general.UNKNOWN_ERROR', { 
        defaultValue: 'An error occurred during sign-up.' 
      })
    });
    
    this.showMessage(translated, 'error');
  }

  // Handle logout specific errors
  handleLogoutError(error: AuthErrorResponse): void {
    const { error: errorData } = error;

    switch (errorData.statusCode) {
      case ERROR_CODES.UNAUTHORIZED:
        // For logout, unauthorized might mean token is already invalid
        // Just show a message, don't logout again
        this.showMessage('Session already expired', 'info');
        break;
      
      case ERROR_CODES.INTERNAL_SERVER_ERROR:
        this.handleServerError(error);
        break;
      
      default:
        this.handleGenericError(error);
    }
  }

  // Public method for handling generic auth errors (used by middleware)
  handleGenericAuthError(error: AuthErrorResponse): void {
    const { error: errorData } = error;

    switch (errorData.statusCode) {
      case ERROR_CODES.UNAUTHORIZED:
        this.handleUnauthorizedError(error);
        break;
      
      case ERROR_CODES.VALIDATION_ERROR:
        this.handleValidationError(error);
        break;
      
      case ERROR_CODES.INTERNAL_SERVER_ERROR:
        this.handleServerError(error);
        break;
      
      case ERROR_CODES.BAD_REQUEST:
        this.handleBadRequestError(error);
        break;
      
      default:
        this.handleGenericError(error);
    }
  }

  // Handle unauthorized errors (401)
  private handleUnauthorizedError(error: AuthErrorResponse | Record<string, unknown>): void {
    const message = this.getSpecificAuthMessage(error);
    
    // Show error message
    this.showMessage(message, 'error');
    
    // Logout user after showing message
    setTimeout(() => {
      this.api.dispatch(logout());
    }, 2000);
  }

  // Handle validation errors (422)
  private handleValidationError(error: AuthErrorResponse | Record<string, unknown>): void {
    const message = this.getSpecificAuthMessage(error);
    this.showMessage(message, 'error');
  }

  // Handle bad request errors (400)
  private handleBadRequestError(error: AuthErrorResponse | Record<string, unknown>): void {
    const message = this.getSpecificAuthMessage(error);
    this.showMessage(message, 'error');
  }

  // Handle server errors (500+)
  private handleServerError(error: AuthErrorResponse | Record<string, unknown>): void {
    const message = getUserFriendlyMessage(error as AuthErrorResponse);
    this.showMessage(message, 'error');
  }

  // Handle generic errors
  private handleGenericError(error: AuthErrorResponse | Record<string, unknown>): void {
    // For auth errors, use specific auth message instead of generic one
    const message = this.getSpecificAuthMessage(error);
    this.showMessage(message, 'error');
  }

  // Get specific authentication error messages using i18n
  // ALWAYS use code + domain for i18n lookup, NOT the backend message
  // Uses centralized auth error config (single source of truth)
  private getSpecificAuthMessage(error: AuthErrorResponse | Record<string, unknown>): string {
    // RTK Query reject payload: { status, data: { status, error, path, ... } } OR flat API body
    const err = error as Record<string, unknown>;
    const data = err?.data as Record<string, unknown> | undefined;
    const errorData =
      (data?.error as Record<string, unknown> | undefined) ??
      (err?.error as Record<string, unknown> | undefined) ??
      (data && typeof data.code === 'string' ? data : undefined);
    const code = (errorData as { code?: string } | undefined)?.code;
    const details = (errorData as { details?: string } | undefined)?.details;

    const mapping = code ? getAuthErrorMapping(code, details) : null;

    const errorKey = mapping
      ? mapping.messageKey
      : getErrorMessageKey(error);

    return i18n.t(`error:${errorKey}`, {
      defaultValue: i18n.t('error:general.UNKNOWN_ERROR', {
        defaultValue: 'Authentication failed. Please try again.',
      }),
    });
  }

  // Show message using the message slice
  private showMessage(value: string, type: 'error' | 'info' | 'success' | 'warning'): void {
    this.api.dispatch(setMessage({
      value,
      timeout: type === 'error' ? 8000 : 5000,
      type,
    }));
  }
}
